import { formatDuration } from './formatters'
import {
  type CriticalAction,
  type CriticalActionDetailItem,
  type CriticalActionDetailSection,
  type CriticalActionResult,
  type CriticalSubActionEvent,
  type Flow,
  type PlayerHintEvent,
  type PlayerRun,
  type RunResult,
  type ScoringRubric,
  type Stage,
  type StageResult,
  type SupplySelectionEvent
} from './model'

export function toStages(flow: Flow | undefined, rubric: ScoringRubric | undefined): Stage[] | undefined {
  if (!flow) {
    return undefined
  }
  const criticalActions = rubric?.criticalActions ?? []
  return flow.stages.map(stage => ({
    id: stage.id,
    name: stage.name,
    criticalActions: criticalActions
      .filter(criticalAction => criticalAction.stageId === stage.id)
      .map(criticalAction => ({
        id: criticalAction.id,
        description: criticalAction.displayName,
        domainsOfExcellence: criticalAction.domainsOfExcellence
      }))
  }))
}

export function toRunResult(playerRun: PlayerRun, stages: Stage[]): RunResult {
  const criticalActionsById = new Map(playerRun.criticalActions.map(x => [x.id, x]))
  const supplySelectionsByCriticalActionId = new Map(playerRun.supplySelections.map(x => [x.id, x]))
  const criticalSubActionsById = new Map(playerRun.criticalSubActions.map(x => [`${x.parent}:${x.id}`, x]))
  const hintsByCriticalAction = new Map<string, PlayerHintEvent[]>()
  for (const hint of playerRun.hints) {
    if (hint.criticalAction) {
      const hints = hintsByCriticalAction.get(hint.criticalAction) ?? []
      hints.push(hint)
      hintsByCriticalAction.set(hint.criticalAction, hints)
    }
  }

  let totalCriticalActions = 0
  let passedCriticalActions = 0

  const stageResults: StageResult[] = stages.map(stage => {
    let allPassed = true
    let stageHints: PlayerHintEvent[] = []

    const criticalActionResults: CriticalActionResult[] = stage.criticalActions.map(criticalAction => {
      const completed = criticalActionsById.get(criticalAction.id)
      const passed = completed?.passed ?? false
      if (passed) {
        passedCriticalActions++
      } else {
        allPassed = false
      }

      const criticalActionHints = hintsByCriticalAction.get(criticalAction.id) ?? []
      stageHints = [...stageHints, ...criticalActionHints]

      const criticalActionDetails = getCriticalActionDetails(
        criticalAction,
        supplySelectionsByCriticalActionId,
        criticalSubActionsById
      )

      return {
        passed,
        criticalAction,
        hints: criticalActionHints,
        criticalActionDetails
      }
    })

    totalCriticalActions += criticalActionResults.length

    return {
      title: stage.name,
      criticalActionResults,
      hints: stageHints,
      passed: allPassed
    }
  })

  return {
    id: playerRun.id,
    score: playerRun.score,
    baseScore: playerRun.baseScore,
    passed: playerRun.passed,
    isTest: playerRun.isTest,
    completed: playerRun.completed,
    time: playerRun.time,
    hintsUsed: playerRun.hintsUsed,
    failedContaminationCriticalActionCount: playerRun.failedContaminationCriticalActionCount,
    courseId: playerRun.courseId,
    multiplayerSessionId: playerRun.multiplayerSessionId,
    exitModule: playerRun.exitModule,
    stageResults,
    passedCriticalActions,
    totalCriticalActions,
    formattedDuration: formatDuration(playerRun.time),
    techniqueId: playerRun.techniqueId
  }
}

export function getScoreColor(correct: number, total: number) {
  return correct === total ? 'text-green' : correct === 0 ? 'text-red' : 'text-yellow-darker'
}

function getCriticalActionDetails(
  criticalAction: CriticalAction,
  supplySelectionsByCriticalActionId: Map<string, SupplySelectionEvent>,
  criticalSubActionsById: Map<string, CriticalSubActionEvent>
): CriticalActionDetailSection[] | undefined {
  const details: CriticalActionDetailSection[] = []

  const supplySelectionDetails = getSupplySelectionDetails(criticalAction, supplySelectionsByCriticalActionId)
  if (supplySelectionDetails?.length) {
    details.push(...supplySelectionDetails)
  }

  if (criticalAction.id === 'SafetyCheck' && criticalAction.criticalSubActions?.length) {
    details.push(getSubCriticalActionDetails(criticalAction, criticalSubActionsById))
  }

  return details.length ? details : undefined
}

function getSupplySelectionDetails(
  criticalAction: CriticalAction,
  supplySelectionsByCriticalActionId: Map<string, SupplySelectionEvent>
): CriticalActionDetailSection[] | undefined {
  const subCriticalActionsWithSupplySelection = criticalAction.criticalSubActions?.filter(
    subCriticalAction => subCriticalAction.supplySelectionItems?.length
  )
  if (!subCriticalActionsWithSupplySelection?.length) {
    return undefined
  }

  const actualItemsPlaced = supplySelectionsByCriticalActionId.get(criticalAction.id)?.itemsPlaced ?? []

  const supplySelectionItems = subCriticalActionsWithSupplySelection.flatMap(
    (subCriticalAction): CriticalActionDetailItem[] => {
      const subCriticalActionItems = subCriticalAction.supplySelectionItems ?? []
      const selectedSubCriticalActionItems = actualItemsPlaced
        .map(itemId => subCriticalActionItems.find(item => item.id === itemId))
        .filter(item => !!item)

      const expectedCorrectItem = subCriticalActionItems.find(item => item.correct)

      if (expectedCorrectItem) {
        const selectedIncorrectItem = selectedSubCriticalActionItems.find(item => !item.correct)
        if (selectedIncorrectItem) {
          return [
            {
              id: subCriticalAction.id,
              expectedLabel: expectedCorrectItem.name,
              label: selectedIncorrectItem.name,
              status: 'incorrect'
            }
          ]
        }

        const selectedCorrectItem = selectedSubCriticalActionItems.find(item => item.id === expectedCorrectItem.id)
        if (selectedCorrectItem) {
          return [
            {
              id: subCriticalAction.id,
              expectedLabel: expectedCorrectItem.name,
              label: selectedCorrectItem.name,
              status: 'correct'
            }
          ]
        }

        return [
          {
            id: subCriticalAction.id,
            expectedLabel: expectedCorrectItem.name,
            label: 'No selection made',
            status: 'incomplete'
          }
        ]
      }

      return selectedSubCriticalActionItems
        .filter(item => !item.correct)
        .map(item => ({
          id: `${subCriticalAction.id}-${item.id}`,
          label: item.name,
          status: 'extra'
        }))
    }
  )

  const items = supplySelectionItems.filter(x => x.status !== 'extra')
  const extraItems = supplySelectionItems.filter(x => x.status === 'extra')

  const sections: CriticalActionDetailSection[] = []

  if (items.length) {
    sections.push({
      title: 'Supply selections',
      items
    })
  }

  if (extraItems.length) {
    sections.push({
      title: `Other incorrect selection${extraItems.length > 1 ? 's' : ''}`,
      items: extraItems
    })
  }

  if (!sections.length) {
    return undefined
  }

  return sections
}

function getSubCriticalActionDetails(
  criticalAction: CriticalAction,
  criticalSubActionsById: Map<string, CriticalSubActionEvent>
): CriticalActionDetailSection {
  return {
    items: criticalAction.criticalSubActions!.map(subCriticalAction => {
      const completedSubCriticalAction = criticalSubActionsById.get(`${criticalAction.id}:${subCriticalAction.id}`)
      return {
        id: subCriticalAction.id,
        label: subCriticalAction.description,
        status: completedSubCriticalAction
          ? completedSubCriticalAction.passed
            ? 'correct'
            : 'incorrect'
          : 'incomplete'
      }
    })
  }
}
